import { useState } from 'react'
import { BarChart3, Check, ChevronRight, Filter, Plus, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useRole } from '@/app/role'
import { PageHeader } from '@/app/shell/PageHeader'
import { useFleet, type FleetRow } from '@/lib/api/hooks'
import { useFleetOps } from '@/lib/api/operations'
import { CreateSiteDialog } from './CreateSiteDialog'
import type { Indices } from '@/lib/api/types'
import { riskColor, tok } from '@/charts'
import { Button } from '@/ui/button'
import { Card, KLabel } from '@/ui/card'
import { RiskChip } from '@/ui/chip'
import { MetricTile } from '@/ui/metric-tile'
import { PackBadge } from '@/ui/pack-badge'
import { LoadingBlock } from '@/ui/states'
import { cn } from '@/lib/utils'

type Pack = 'BESS' | 'DSEAR'
const METRICS: [key: keyof Indices, label: string, higherWorse: boolean][] = [
  ['control_confidence_percent', 'Control confidence', false],
  ['risk_score', 'RiskScore', true],
  ['loss_probability_percent', 'Loss probability %', true],
  ['loss_driver_index', 'Loss driver', true],
  ['explosion_escalation_potential', 'Escalation potential', true],
  ['dependency_concentration', 'Dependency conc.', true],
  ['recovery_fragility', 'Recovery fragility', true],
  ['protection_validity', 'Protection validity', false],
]

export function PortfolioPage() {
  const nav = useNavigate()
  const { role } = useRole()
  const { rows, isLoading } = useFleet()
  const opsFleet = useFleetOps()
  const [showCreate, setShowCreate] = useState(false)

  const [packs, setPacks] = useState<Set<Pack>>(new Set<Pack>(['BESS', 'DSEAR']))
  const [criticalOnly, setCriticalOnly] = useState(false)
  const [filterOpen, setFilterOpen] = useState(false)
  const [compare, setCompare] = useState(false)
  const filtersActive = packs.size < 2 || criticalOnly

  const filtered = rows.filter((r) => packs.has(r.pack) && (!criticalOnly || r.indices?.critical_risk_flag))
  const cc = (r: FleetRow) => r.indices?.control_confidence_percent ?? 0
  const withCC = filtered.filter((r) => r.indices?.control_confidence_percent != null)
  const critical = filtered.filter((r) => r.indices?.critical_risk_flag).length
  const avgCC = withCC.length ? Math.round(withCC.reduce((a, r) => a + cc(r), 0) / withCC.length) : 0
  const weakest = [...withCC].sort((a, b) => cc(a) - cc(b))

  const togglePack = (p: Pack) =>
    setPacks((prev) => {
      const next = new Set(prev)
      if (next.has(p)) next.delete(p)
      else next.add(p)
      return next.size ? next : prev
    })

  return (
    <>
      <PageHeader
        title="Portfolio"
        subtitle={
          <>
            <span>{filtered.length} sites</span>
            <span className="text-faint">·</span>
            <span>{[...packs].join(' + ')}</span>
            <span className="text-faint">·</span>
            <span>{critical} critical</span>
          </>
        }
        actions={
          <>
            {role === 'assessor' && (
              <Button size="sm" onClick={() => setShowCreate(true)}>
                <Plus /> New site
              </Button>
            )}
            <div className="relative">
              <Button variant="secondary" size="sm" onClick={() => setFilterOpen((o) => !o)}>
                <Filter /> Filter{filtersActive && <span className="size-1.5 rounded-full bg-primary" />}
              </Button>
              {filterOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setFilterOpen(false)} />
                  <div className="absolute right-0 z-40 mt-2 w-52 rounded-lg border bg-popover p-2 shadow-xl">
                    <div className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-faint">Pack</div>
                    {(['BESS', 'DSEAR'] as Pack[]).map((p) => (
                      <button key={p} onClick={() => togglePack(p)} className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-secondary">
                        <span className={cn('grid size-4 place-items-center rounded border', packs.has(p) ? 'border-primary bg-primary text-primary-foreground' : 'border-input')}>
                          {packs.has(p) && <Check className="size-3" />}
                        </span>
                        {p}
                      </button>
                    ))}
                    <button onClick={() => setCriticalOnly((c) => !c)} className="mt-1 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-secondary">
                      <span className={cn('grid size-4 place-items-center rounded border', criticalOnly ? 'border-primary bg-primary text-primary-foreground' : 'border-input')}>
                        {criticalOnly && <Check className="size-3" />}
                      </span>
                      Critical only
                    </button>
                  </div>
                </>
              )}
            </div>
            <Button variant={compare ? 'default' : 'secondary'} size="sm" onClick={() => setCompare((c) => !c)}>
              <BarChart3 /> Compare
            </Button>
          </>
        }
      />

      {isLoading ? (
        <Card>
          <LoadingBlock lines={5} />
        </Card>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <MetricTile label="Sites" value={String(filtered.length)} tone={tok('--stable')} state={[...packs].join(' + ')} />
            <MetricTile label="Critical flags" value={String(critical)} tone={critical > 0 ? tok('--ignition') : tok('--stable')} state={critical > 0 ? 'review now' : 'clear'} />
            <MetricTile label="Avg control conf." value={String(avgCC)} unit="/100" tone={riskColor(avgCC)} state="across scored sites" />
            <MetricTile label="Assets streaming" value={String(opsFleet.data?.assets_streaming ?? '—')} tone={tok('--clear')} state="SCADA live" />
          </div>

          {compare && (
            <Card className="mt-4 overflow-x-auto">
              <div className="mb-3 flex items-center justify-between">
                <KLabel>Compare — engine indices</KLabel>
                <button onClick={() => setCompare(false)} className="text-faint hover:text-foreground">
                  <X className="size-4" />
                </button>
              </div>
              {withCC.length === 0 ? (
                <p className="text-sm text-muted-foreground">No scored sites to compare in the current filter.</p>
              ) : (
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="text-left">
                      <th className="py-2 pr-4 text-[10px] font-semibold uppercase tracking-wider text-faint">Metric</th>
                      {withCC.map((r) => (
                        <th key={r.id} className="px-3 py-2 text-[12px] font-semibold">{r.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {METRICS.map(([key, label, hw]) => (
                      <tr key={key} className="border-t">
                        <td className="py-2 pr-4 text-muted-foreground">{label}</td>
                        {withCC.map((r) => {
                          const v = Number(r.indices?.[key] ?? 0)
                          return (
                            <td key={r.id} className="tabular px-3 py-2 font-semibold" style={{ color: riskColor(hw ? 100 - v : v) }}>
                              {v}
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          )}

          {weakest.length > 0 && (
            <Card className="mt-4">
              <KLabel className="mb-3">Weakest sites — control confidence</KLabel>
              <div className="space-y-2.5">
                {weakest.map((r) => (
                  <button key={r.id} onClick={() => nav(`/site/${r.id}/underwriting`)} className="flex w-full items-center gap-3 text-left">
                    <span className="w-40 truncate text-[12.5px]">{r.name}</span>
                    <span className="h-2 flex-1 rounded bg-surface-2">
                      <span className="block h-full rounded" style={{ width: `${cc(r)}%`, background: riskColor(cc(r)) }} />
                    </span>
                    <span className="tabular w-7 text-[12.5px] font-semibold" style={{ color: riskColor(cc(r)) }}>{cc(r)}</span>
                  </button>
                ))}
              </div>
            </Card>
          )}

          <div className="mb-3 mt-6 font-display text-[13px] font-semibold">All sites</div>
          <Card className="overflow-hidden p-0">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-faint">
                  <th className="px-4 py-3 font-semibold">Site</th>
                  <th className="px-4 py-3 font-semibold">Pack</th>
                  <th className="px-4 py-3 font-semibold" style={{ width: '24%' }}>Control confidence</th>
                  <th className="px-4 py-3 font-semibold">Loss prob.</th>
                  <th className="px-4 py-3 font-semibold">Drift</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="cursor-pointer border-t hover:bg-surface-2" onClick={() => nav(`/site/${r.id}/underwriting`)}>
                    <td className="px-4 py-2.5 font-medium">
                      {r.name}
                      {r.indices?.critical_risk_flag && (
                        <span className="ml-2 inline-flex items-center gap-1 rounded bg-ignition/[0.14] px-1.5 py-px text-[10.5px] font-semibold text-ignition">critical</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <PackBadge pack={r.pack} />
                    </td>
                    <td className="px-4 py-2.5">
                      {r.degraded ? (
                        <span className="text-[12px] text-faint">engine metadata in progress</span>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="h-2 w-24 rounded bg-surface-2">
                            <span className="block h-full rounded" style={{ width: `${cc(r)}%`, background: riskColor(cc(r)) }} />
                          </span>
                          <span className="tabular text-[12px] font-semibold" style={{ color: riskColor(cc(r)) }}>{cc(r)}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5">{r.indices?.loss_probability_label ?? <span className="text-faint">—</span>}</td>
                    <td className="px-4 py-2.5">
                      {r.indices?.drift_state ? (
                        <RiskChip label={r.indices.drift_state} tone={/critical|declin|latent/i.test(r.indices.drift_state) ? tok('--flare') : tok('--stable')} />
                      ) : (
                        <span className="text-faint">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <ChevronRight className="size-4 text-faint" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
      {showCreate && <CreateSiteDialog onClose={() => setShowCreate(false)} />}
    </>
  )
}
